import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import React, { useCallback, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  TableSortLabel,
  Stack,
  IconButton,
  MenuItem,
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import ContactsIcon from "@mui/icons-material/Contacts";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  TABLE_STYLES,
  TABLE_EXPLORE_MENU_ITEM_SX,
  TABLE_ICON_BLUE_SX,
  TABLE_ICON_GREEN_SX,
  TABLE_ROW_HOVER_SX,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";
import {
  normalizeCountry,
  normalizeState,
  normalizeString,
} from "../../../utils/filterNormalization";

import {
  formatLocationLabel,
  getProspectingCompanyWebsiteUrl,
} from "./ProspectingCompaniesHelpers";
import { getSectorDisplayParts } from "../../../constants/sectors";
import { formatBroadSectorLabel } from "../../bre/companies/BRECompanyHelpers";

const EXPLORE_COLUMN_WIDTH = 88;
const NAME_COLUMN_WIDTH = 260;
const TAGS_COLUMN_WIDTH = 200;
const LOCATION_COLUMN_WIDTH = 220;
const SECTOR_COLUMN_WIDTH = 190;
const NAICS_COLUMN_WIDTH = 220;
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const STATUS_CONFIG = {
  "New - Needs Outreach":   { bg: "#FFF3E0", color: "#B45309", border: "#FCD34D" },
  "Outreach Sent":          { bg: "#E0F2FE", color: "#0369A1", border: "#7DD3FC" },
  "In Conversation":        { bg: "#DBEAFE", color: "#1D4ED8", border: "#93C5FD" },
  "Qualified Opportunity":  { bg: "#EDE9FE", color: "#6D28D9", border: "#C4B5FD" },
  "Responded to RFI":       { bg: "#DCFCE7", color: "#15803D", border: "#86EFAC" },
  "Closed Won":             { bg: "#ECFDF5", color: "#047857", border: "#6EE7B7" },
  "Closed Lost":            { bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5" },
  "Nurture / Not Now":      { bg: "#F1F5F9", color: "#64748B", border: "#CBD5E1" },
};
const NO_STATUS_CONFIG = { bg: "#F3F4F6", color: "#9CA3AF", border: "#E5E7EB" };
const STATUS_TOOLTIPS = {
  "New - Needs Outreach": "A newly identified prospect that has not been contacted yet.",
  "Outreach Sent": "Initial outreach has been sent and the team is waiting for a response.",
  "In Conversation": "The prospect has responded and active discussion is underway.",
  "Qualified Opportunity": "The prospect has a defined project or need worth active pursuit.",
  "Responded to RFI": "The team has submitted a response to the prospect's request for information.",
  "Closed Won": "The prospect converted into a successful opportunity.",
  "Closed Lost": "The opportunity did not move forward.",
  "Nurture / Not Now": "The prospect is not active now but should remain in future follow-up.",
};
const DEFAULT_STATUS_TOOLTIP = "Current prospect pipeline status.";

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiInputBase-input": {
    py: 0.75,
    color: "#00336C",
    fontWeight: 700,
  },
  "& input": {
    color: "#00336C !important",
    WebkitTextFillColor: "#00336C",
    caretColor: "#00336C",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const isFilterActive = (value, filterKey, filters) => {
  if (filterKey === "name") return Boolean(filters?.pipeline_stage);
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

function ProspectingCompaniesTable({
  companies,
  columns,

  orderBy,
  orderDirection,
  onSort,

  isSelected,
  toggleOne,
  toggleMany,

  currentPageIds,
  allCurrentSelected,
  someCurrentSelected,

  handleUpdateStage,

  navigate,
  setSelectedCompany,
  setProfileOpen,

  PIPELINE_STAGE_OPTIONS,
  renderCompanyTags,
  filters,
  onFilterChange,
  onLocationFilterChange,
  uniqueValues,
  uniqueTagValues,
}) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  const [activeExploreCompany, setActiveExploreCompany] = useState(null);
  const [statusChangeAnchor, setStatusChangeAnchor] = useState(null);
  const [statusChangeCompany, setStatusChangeCompany] = useState(null);

  const isExploreMenuOpen = Boolean(exploreAnchorEl);

  const locationOptions = useMemo(
    () =>
      Array.from(
        new Map(
          companies
            .map((company) => {
              const city = normalizeString(company?.city);
              const state = normalizeState(company?.state);
              const country = normalizeCountry(company?.country);
              const label = formatLocationLabel({ city, state, country });

              if (label === "—") {
                return null;
              }

              const key = [city, state, country].join("|");
              return [key, { key, label, city, state, country }];
            })
            .filter(Boolean),
        ).values(),
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [companies],
  );
  const { options: sectorOptions } = useNaicsSectors(uniqueValues("sector"));
  const naicsOptions = useMemo(
    () => uniqueValues("naics_name"),
    [uniqueValues],
  );
  const statusOptions = useMemo(
    () => PIPELINE_STAGE_OPTIONS,
    [PIPELINE_STAGE_OPTIONS],
  );

  const lowerSearch = filterSearch.trim().toLowerCase();
  const filteredTagOptions = useMemo(
    () =>
      (uniqueTagValues || []).filter((option) =>
        option.toLowerCase().includes(lowerSearch),
      ),
    [lowerSearch, uniqueTagValues],
  );
  const filteredLocationOptions = useMemo(
    () =>
      locationOptions.filter((option) =>
        option.label.toLowerCase().includes(lowerSearch),
      ),
    [locationOptions, lowerSearch],
  );
  const filteredSectorOptions = useMemo(
    () =>
      sectorOptions.filter((option) =>
        formatBroadSectorLabel(option).toLowerCase().includes(lowerSearch),
      ),
    [lowerSearch, sectorOptions],
  );
  const filteredNaicsOptions = useMemo(
    () =>
      naicsOptions.filter((option) =>
        option.toLowerCase().includes(lowerSearch),
      ),
    [lowerSearch, naicsOptions],
  );

  const handleOpenExploreMenu = useCallback((event, company) => {
    setExploreAnchorEl(event.currentTarget);
    setActiveExploreCompany(company);
  }, []);

  const handleCloseExploreMenu = useCallback(() => {
    setExploreAnchorEl(null);
    setActiveExploreCompany(null);
  }, []);

  const openFilterMenu = useCallback((event, filterKey) => {
    setActiveFilter(filterKey);
    setFilterAnchor(event.currentTarget);
    setFilterSearch("");
  }, []);

  const closeFilterMenu = useCallback(() => {
    setActiveFilter(null);
    setFilterAnchor(null);
    setFilterSearch("");
  }, []);

  const exploreActions = useMemo(() => {
    if (!activeExploreCompany) return [];

    const linkActions = [
      {
        key: "website",
        label: "Website",
        icon: <LanguageIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.website,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: <FacebookIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.facebook,
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        icon: <LinkedInIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.linkedin,
      },
      {
        key: "twitter",
        label: "Twitter / X",
        icon: <TwitterIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.twitter,
      },
    ]
      .filter((action) => action.href)
      .map((action) => ({
        ...action,
        onClick: () =>
          window.open(
            getProspectingCompanyWebsiteUrl(action.href),
            "_blank",
            "noopener,noreferrer",
          ),
      }));

    return [
      {
        key: "profile",
        label: "Company Profile",
        icon: <BusinessIcon sx={{ ...TABLE_ICON_GREEN_SX, fontSize: 22 }} />,
        onClick: () => {
          setSelectedCompany(activeExploreCompany);
          setProfileOpen(true);
        },
      },
      {
        key: "contacts",
        label: "Contacts",
        icon: <ContactsIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        onClick: () =>
          navigate(`/prospecting-contacts?company_id=${activeExploreCompany.id}`),
      },
      ...linkActions,
    ];
  }, [activeExploreCompany, navigate, setProfileOpen, setSelectedCompany]);

  const renderFilterHeader = ({
    label,
    filterKey,
    sortable = false,
    sortKey = filterKey,
    active = false,
    minWidth,
  }) => {
    const minWidthByFilterKey = {
      location: 124,
      sector: 132,
      naics_name: 140,
      name: 108,
    };

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%" }}
      >
        <Button
          variant="text"
          onClick={(event) => openFilterMenu(event, filterKey)}
          sx={{
            ...HEADER_TRIGGER_SX,
            minWidth: minWidth || minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
            color: active ? "#648315" : "#00336C",
            border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
            backgroundColor: "rgba(255,255,255,0.92)",
            "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
          }}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
        >
          {label}
        </Button>
        {sortable && (
          <TableSortLabel
            active={orderBy === sortKey}
            direction={orderBy === sortKey ? orderDirection : "asc"}
            onClick={() => onSort(sortKey)}
            sx={{
              "& .MuiTableSortLabel-icon": {
                fontSize: "1rem",
              },
            }}
          />
        )}
      </Stack>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "name") {
      return (
        <Menu {...commonMenuProps}>
          <MenuItem
            selected={orderBy === "name" && orderDirection === "asc"}
            onClick={() => { onSort("name", "asc"); closeFilterMenu(); }}
          >
            Name A → Z
          </MenuItem>
          <MenuItem
            selected={orderBy === "name" && orderDirection === "desc"}
            onClick={() => { onSort("name", "desc"); closeFilterMenu(); }}
          >
            Name Z → A
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <Typography
            sx={{ px: 2, py: 0.5, fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}
          >
            Filter by Status
          </Typography>
          <MenuItem
            selected={!filters.pipeline_stage}
            onClick={() => { onFilterChange("pipeline_stage", ""); closeFilterMenu(); }}
          >
            All Statuses
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem
              key={option}
              selected={filters.pipeline_stage === option}
              onClick={() => { onFilterChange("pipeline_stage", option); closeFilterMenu(); }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "tags") {
      const selectedTags = Array.isArray(filters.tags) ? filters.tags : [];

      return (
        <Menu {...commonMenuProps}>
          {searchField("Search tags")}
          <MenuItem
            selected={selectedTags.length === 0}
            onClick={() => onFilterChange("tags", [])}
          >
            All Tags
          </MenuItem>
          {filteredTagOptions.map((option) => {
            const checked = selectedTags.includes(option);

            return (
              <MenuItem
                key={option}
                selected={checked}
                onClick={() =>
                  onFilterChange(
                    "tags",
                    checked
                      ? selectedTags.filter((value) => value !== option)
                      : [...selectedTags, option],
                  )
                }
              >
                {option}
              </MenuItem>
            );
          })}
        </Menu>
      );
    }

    if (activeFilter === "location") {
      const normalizedCity = normalizeString(filters.city);
      const normalizedState = normalizeState(filters.state);
      const normalizedCountry = normalizeCountry(filters.country);

      return (
        <Menu {...commonMenuProps}>
          {searchField("Search location")}
          <MenuItem
            selected={!filters.city && !filters.state && !filters.country}
            onClick={() => {
              onLocationFilterChange({ city: "", state: "", country: "" });
              closeFilterMenu();
            }}
          >
            All Locations
          </MenuItem>
          {filteredLocationOptions.map((option) => {
            const selected =
              normalizedCity === option.city &&
              normalizedState === option.state &&
              normalizedCountry === option.country;

            return (
              <MenuItem
                key={option.key}
                selected={selected}
                onClick={() => {
                  onLocationFilterChange({
                    city: option.city,
                    state: option.state,
                    country: option.country,
                  });
                  closeFilterMenu();
                }}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </Menu>
      );
    }

    if (activeFilter === "sector") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search sector")}
          <MenuItem
            selected={!filters.sector}
            onClick={() => {
              onFilterChange("sector", "");
              closeFilterMenu();
            }}
          >
            All Sectors
          </MenuItem>
          {filteredSectorOptions.map((option) => (
            <MenuItem
              key={option}
              selected={filters.sector === option}
              onClick={() => {
                onFilterChange("sector", option);
                closeFilterMenu();
              }}
            >
              {formatBroadSectorLabel(option)}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "naics_name") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search NAICS")}
          <MenuItem
            selected={!filters.naics_name}
            onClick={() => {
              onFilterChange("naics_name", "");
              closeFilterMenu();
            }}
          >
            All NAICS
          </MenuItem>
          {filteredNaicsOptions.map((option) => (
            <MenuItem
              key={option}
              selected={normalizeString(filters.naics_name) === option}
              onClick={() => {
                onFilterChange("naics_name", option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 1280, md: 1430 },
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
        {/* ===== HEAD ===== */}
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              padding="checkbox"
              sx={{ ...HEAD_CELL_SX, width: 48 }}
            >
              <Checkbox
                indeterminate={someCurrentSelected && !allCurrentSelected}
                checked={currentPageIds.length > 0 && allCurrentSelected}
                onChange={() => toggleMany(currentPageIds)}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                indeterminateIcon={<RemoveCircleIcon />}
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                }}
              />
            </TableCell>

            {columns.map((col) => (
              <TableCell
                key={col.id}
                align="center"
                sx={{
                  ...HEAD_CELL_SX,
                  ...(col.id === "name" && {
                    width: NAME_COLUMN_WIDTH,
                    maxWidth: NAME_COLUMN_WIDTH,
                  }),
                  ...(col.id === "tags" && {
                    width: TAGS_COLUMN_WIDTH,
                    maxWidth: TAGS_COLUMN_WIDTH,
                  }),
                  ...(col.id === "location" && {
                    width: LOCATION_COLUMN_WIDTH,
                    maxWidth: LOCATION_COLUMN_WIDTH,
                  }),
                  ...(col.id === "sector" && {
                    width: SECTOR_COLUMN_WIDTH,
                    maxWidth: SECTOR_COLUMN_WIDTH,
                  }),
                  ...(col.id === "naics_name" && {
                    width: NAICS_COLUMN_WIDTH,
                    maxWidth: NAICS_COLUMN_WIDTH,
                  }),
                  ...(col.id === "explore" && {
                    width: EXPLORE_COLUMN_WIDTH,
                    minWidth: EXPLORE_COLUMN_WIDTH,
                    maxWidth: EXPLORE_COLUMN_WIDTH,
                  }),
                }}
              >
                {col.id === "name"
                  ? renderFilterHeader({
                      label: col.label,
                      filterKey: "name",
                      active: isFilterActive(filters.pipeline_stage, "name", filters),
                    })
                  : col.id === "tags"
                    ? renderFilterHeader({
                        label: col.label,
                        filterKey: "tags",
                        active: isFilterActive(filters.tags),
                      })
                    : col.id === "location"
                      ? renderFilterHeader({
                          label: col.label,
                          filterKey: "location",
                          active:
                            isFilterActive(filters.city) ||
                            isFilterActive(filters.state) ||
                            isFilterActive(filters.country),
                        })
                      : col.id === "sector"
                        ? renderFilterHeader({
                            label: col.label,
                            filterKey: "sector",
                            sortable: true,
                            active: isFilterActive(filters.sector),
                          })
                        : col.id === "naics_name"
                          ? renderFilterHeader({
                              label: col.label,
                              filterKey: "naics_name",
                              sortable: true,
                              active: isFilterActive(filters.naics_name),
                            })
                          : col.sortable ? (
                              <TableSortLabel
                                active={orderBy === col.id}
                                direction={orderBy === col.id ? orderDirection : "asc"}
                                onClick={() => onSort(col.id)}
                                sx={{
                                  color: "#ffffff !important",
                                  "&:hover": { color: "#ffffff !important" },
                                  "&.Mui-active": { color: "#ffffff !important" },
                                  "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                                }}
                              >
                                {col.label}
                              </TableSortLabel>
                            ) : (
                              col.label
                            )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* ===== BODY ===== */}
        <TableBody sx={{ background: "white" }}>
          {companies.map((company, index) => {
            const idStr = company?.id ? String(company.id) : null;
            const naicsDisplay =
              company.naics_name ||
              company.primary_naics_bucket ||
              company.naics_code ||
              "";

            const baseStriped = index % 2 === 0 ? "#fafafa" : "white";
            const statusLabel = company.pipeline_stage || "New - Needs Outreach";
            const statusCfg = STATUS_CONFIG[statusLabel] || NO_STATUS_CONFIG;

            return (
              <TableRow
                key={company.id ?? index}
                sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: baseStriped, "&:hover": { backgroundColor: "#eef2f7" } }}
              >
                {/* Checkbox */}
                <TableCell align="center" padding="checkbox">
                  <Checkbox
                    disabled={!idStr}
                    checked={idStr ? isSelected(idStr) : false}
                    onChange={() => idStr && toggleOne(idStr)}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                  />
                </TableCell>

                {/* Name + status chip */}
                <TableCell
                  align="center"
                  sx={{
                    ...TABLE_STYLES.bodyCell,
                    width: NAME_COLUMN_WIDTH,
                    maxWidth: NAME_COLUMN_WIDTH,
                  }}
                >
                  <Stack direction="column" spacing={0.4} alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Tooltip title={company.name || ""}>
                        <Button
                          variant="text"
                          sx={{
                            ...TABLE_STYLES.clickableCell,
                            p: 0,
                          }}
                          onClick={() =>
                            navigate(`/prospecting-companies/${company.id}`)
                          }
                        >
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: NAME_COLUMN_WIDTH - 40,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {company.name}
                          </span>
                        </Button>
                      </Tooltip>

                      {company.added_to_external_db && (
                        <Tooltip title="Added to External DB">
                          <CloudDoneIcon
                            sx={{ fontSize: 18, color: "#648315" }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    <Tooltip
                      title={STATUS_TOOLTIPS[statusLabel] || DEFAULT_STATUS_TOOLTIP}
                      arrow
                      placement="top"
                    >
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusChangeAnchor(e.currentTarget);
                          setStatusChangeCompany(company);
                        }}
                        endIcon={<ArrowDropDownIcon sx={{ fontSize: "0.8rem !important", ml: "-4px" }} />}
                        sx={{
                          height: 18,
                          fontSize: "0.63rem",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: 10,
                          px: 0.75,
                          py: 0,
                          minWidth: 0,
                          lineHeight: 1.2,
                          backgroundColor: statusCfg.bg,
                          color: statusCfg.color,
                          border: `1px solid ${statusCfg.border}`,
                          "&:hover": {
                            backgroundColor: statusCfg.bg,
                            borderColor: statusCfg.color,
                            opacity: 0.85,
                          },
                          "& .MuiButton-endIcon": { ml: 0.25 },
                        }}
                      >
                        {statusLabel}
                      </Button>
                    </Tooltip>
                    {company.lead_score != null && (
                      <Chip
                        size="small"
                        label={`Lead Score: ${company.lead_score}`}
                        sx={{
                          height: 18,
                          fontSize: "0.63rem",
                          fontWeight: 700,
                          backgroundColor: "#EEF4FB",
                          color: "#00336C",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>

                <TableCell
                  align="left"
                  sx={{ ...TABLE_STYLES.bodyCell, width: TAGS_COLUMN_WIDTH, maxWidth: TAGS_COLUMN_WIDTH }}
                >
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-start" }}>
                    {renderCompanyTags?.(company)}
                  </Box>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ ...TABLE_STYLES.bodyCell, width: LOCATION_COLUMN_WIDTH, maxWidth: LOCATION_COLUMN_WIDTH }}
                >
                  <Tooltip
                    title={formatLocationLabel({
                      city: company.city,
                      state: company.state,
                      country: company.country,
                    })}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: LOCATION_COLUMN_WIDTH,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatLocationLabel({
                        city: company.city,
                        state: company.state,
                        country: company.country,
                      })}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    ...TABLE_STYLES.bodyCell,
                    width: SECTOR_COLUMN_WIDTH,
                    maxWidth: SECTOR_COLUMN_WIDTH,
                  }}
                >
                  <Tooltip
                    title={getSectorDisplayParts(company.sector).standardizedSector || ""}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: SECTOR_COLUMN_WIDTH,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getSectorDisplayParts(company.sector).standardizedSector || "—"}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    ...TABLE_STYLES.bodyCell,
                    width: NAICS_COLUMN_WIDTH,
                    maxWidth: NAICS_COLUMN_WIDTH,
                  }}
                >
                  <Tooltip title={naicsDisplay}>
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: NAICS_COLUMN_WIDTH,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {naicsDisplay || "—"}
                    </span>
                  </Tooltip>
                </TableCell>

                {/* Explore */}
                <TableCell
                  align="center"
                  sx={{
                    ...TABLE_STYLES.bodyCell,
                    position: "sticky",
                    right: 0,
                    backgroundColor: baseStriped,
                    zIndex: 1,
                    width: EXPLORE_COLUMN_WIDTH,
                    minWidth: EXPLORE_COLUMN_WIDTH,
                    maxWidth: EXPLORE_COLUMN_WIDTH,
                    borderLeft: "1px solid #eee",
                    boxShadow: "-2px 0 6px rgba(0,0,0,0.05)",
                  }}
                >
                  <Tooltip title="Explore company">
                    <IconButton
                      size="small"
                      onClick={(event) => handleOpenExploreMenu(event, company)}
                      sx={{ p: 0.5 }}
                    >
                      <TravelExploreIcon sx={TABLE_ICON_BLUE_SX} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          </TableBody>
        </Table>
      </TableContainer>

      {renderFilterMenu()}

      <Menu
        anchorEl={statusChangeAnchor}
        open={Boolean(statusChangeAnchor)}
        onClose={() => { setStatusChangeAnchor(null); setStatusChangeCompany(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 190,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.15)",
              boxShadow: "0 8px 24px rgba(0,51,108,0.12)",
            },
          },
        }}
      >
        {PIPELINE_STAGE_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            selected={statusChangeCompany?.pipeline_stage === opt}
            onClick={() => {
              handleUpdateStage(statusChangeCompany.id, opt);
              setStatusChangeAnchor(null);
              setStatusChangeCompany(null);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={exploreAnchorEl}
        open={isExploreMenuOpen}
        onClose={handleCloseExploreMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 210,
              borderRadius: 2,
              border: "1px solid rgba(0, 51, 108, 0.12)",
              boxShadow: "0 10px 24px rgba(0, 51, 108, 0.16)",
              mt: 0.5,
            },
          },
        }}
      >
        {exploreActions.map((action) => (
          <MenuItem
            key={action.key}
            sx={TABLE_EXPLORE_MENU_ITEM_SX}
            onClick={() => {
              handleCloseExploreMenu();
              action.onClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default React.memo(ProspectingCompaniesTable);
